import {
  AlertTriangle,
  ArrowRight,
  Bug,
  CheckCircle,
  FileText,
  Key,
  Layers,
  Lock,
  Server,
  Shield,
  Siren,
  UserCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
  {
    num: "1",
    icon: Shield,
    title: "Security Overview",
    content: `At Furistic Technologies Inc. ("Furistic," "we," "us," or "our"), security is foundational to everything we build. Our platform serves industries where data integrity and confidentiality are critical — real estate transactions in RealtyOS, financial trading in TradeOS, standards audits in ConformOS, fleet operations in Haulyard, and restaurant orders in Foodium.

This document outlines our security practices, infrastructure architecture, and compliance posture. We maintain a defense-in-depth approach, with multiple layers of security controls protecting our platform, your data, and our AI systems.

Our security program is overseen by our security team, with regular third-party audits and continuous monitoring across all systems.`,
  },
  {
    num: "2",
    icon: CheckCircle,
    title: "Compliance & Certifications",
    content: `Furistic maintains the following compliance standards and certifications:

**Enterprise Security Practices** — Our security controls are aligned with industry best practices covering Security, Availability, Processing Integrity, Confidentiality, and Privacy. We continuously evaluate formal certifications as our compliance requirements evolve.

**PIPEDA Compliance** — As a Canadian company, we comply with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation.

**PCI DSS** — Payment processing is handled through PCI DSS-compliant third-party processors. We do not store, process, or transmit cardholder data directly.

**OWASP Top 10** — Our development practices incorporate protections against the OWASP Top 10 web application security risks, verified through regular penetration testing.

We continuously evaluate additional certifications and frameworks as our compliance requirements evolve.`,
  },
  {
    num: "3",
    icon: Key,
    title: "Encryption Standards",
    content: `All data is protected by industry-standard encryption:

**In Transit.**
• TLS 1.3 for all client-server communications
• Certificate pinning for mobile applications
• HSTS (HTTP Strict Transport Security) enforced across all domains
• Perfect forward secrecy (PFS) enabled on all endpoints

**At Rest.**
• AES-256 encryption for all stored data
• Database-level encryption with managed key rotation
• Encrypted backups with geographically separated storage
• Hardware Security Modules (HSMs) for cryptographic key management

**Application Layer.**
• Bcrypt/Argon2 password hashing with appropriate work factors
• Encrypted API tokens and secrets management via dedicated vault infrastructure
• End-to-end encryption for sensitive document storage
• Signed and encrypted JWT tokens for authentication`,
  },
  {
    num: "4",
    icon: Server,
    title: "Infrastructure Architecture",
    content: `Our platform runs on a modern, cloud-native infrastructure designed for resilience and security:

**Cloud Infrastructure.** Hosted on enterprise-grade cloud providers that maintain recognized industry security certifications. Infrastructure is defined and managed as code for reproducibility and auditability.

**Network Security.**
• Virtual private clouds (VPCs) with strict network segmentation
• Web application firewalls (WAFs) protecting all public endpoints
• DDoS protection and rate limiting at the edge
• Private networking for internal service communication
• Zero-trust network architecture with service mesh

**Container & Orchestration.**
• Containerized workloads running on Kubernetes
• Immutable container images built from hardened base images
• Runtime security monitoring and anomaly detection
• Automated vulnerability scanning of container images

**Availability.**
• Multi-region deployment capability
• Automated failover and disaster recovery procedures
• Regular disaster recovery testing
• RPO (Recovery Point Objective) of 1 hour, RTO (Recovery Time Objective) of 4 hours`,
  },
  {
    num: "5",
    icon: Layers,
    title: "Multi-Product Data Isolation",
    content: `Furistic operates multiple products (RealtyOS, TradeOS, ConformOS, Haulyard, Foodium) on a shared platform. We maintain strict data isolation between products and tenants:

**Product-Level Isolation.**
• Separate database schemas and storage buckets per product
• Independent API endpoints and authentication scopes per product
• Network-level isolation between product services
• Product-specific encryption keys

**Tenant-Level Isolation.**
• Logical data isolation between customers within each product
• Row-level security policies enforced at the database layer
• Tenant-scoped API authentication and authorization
• Audit logging of all cross-tenant access attempts (zero tolerance)

**Shared Identity Layer.**
• Centralized identity and SSO managed through our dedicated identity platform
• Identity data is isolated from product-specific data
• Fine-grained permission scoping per product and per resource
• Multi-factor authentication (MFA) available for all accounts`,
  },
  {
    num: "6",
    icon: UserCheck,
    title: "Access Controls",
    content: `We enforce strict access controls across our platform and internal systems:

**For Customers.**
• Role-based access control (RBAC) with configurable roles and permissions
• Multi-factor authentication (MFA) support, including TOTP and WebAuthn
• Session management with configurable timeouts and concurrent session limits
• API key management with granular scope controls
• Audit logs for all access and modification events

**Internal Controls.**
• Principle of least privilege for all employee access
• Just-in-time (JIT) access provisioning for production systems
• Multi-person approval required for sensitive operations
• Background checks for all employees with access to customer data
• Mandatory security awareness training and annual refreshers
• Quarterly access reviews and deprovisioning audits`,
  },
  {
    num: "7",
    icon: Siren,
    title: "Incident Response",
    content: `Furistic maintains a documented incident response program:

**Detection.** Automated monitoring, alerting, and anomaly detection across all systems. Security Information and Event Management (SIEM) system aggregates and correlates security events.

**Response Phases.**
1. **Identification** — Classify the incident by severity and scope
2. **Containment** — Isolate affected systems to prevent further impact
3. **Eradication** — Remove the threat and patch vulnerabilities
4. **Recovery** — Restore affected systems and verify integrity
5. **Post-Incident Review** — Conduct root cause analysis and implement preventive measures

**Notification.**
• Critical incidents affecting customer data: notification within 72 hours
• Service-impacting incidents: real-time updates via status page
• Post-incident reports provided for significant events
• Compliance with PIPEDA breach notification requirements

**Testing.** We conduct regular tabletop exercises and simulated incident response drills to ensure team readiness.`,
  },
  {
    num: "8",
    icon: Bug,
    title: "Responsible Disclosure Program",
    content: `We welcome security researchers to help us keep the Furistic platform safe. If you discover a security vulnerability, please report it responsibly.

**How to Report.**
• Email: security@furistic.co
• Include detailed steps to reproduce the vulnerability
• Provide your assessment of the potential impact
• Allow reasonable time for us to investigate and remediate before public disclosure

**Scope.**
• All Furistic web applications and APIs (*.furistic.co)
• RealtyOS, TradeOS, ConformOS, Haulyard, and Foodium applications
• Furistic mobile applications
• Authentication and identity systems

**Out of Scope.**
• Social engineering attacks against Furistic employees
• Physical attacks against Furistic infrastructure
• Denial of service (DoS/DDoS) attacks
• Automated scanning without prior coordination

**Our Commitment.**
• Acknowledge receipt of your report within 48 hours
• Provide an initial assessment within 5 business days
• Keep you informed of remediation progress
• Credit researchers in our security acknowledgments (with your consent)
• We will not take legal action against researchers who act in good faith and comply with this program

We do not currently offer monetary bounties but may do so in the future.`,
  },
];

export default function SecurityPage() {
  return (
    <main className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <section className="relative pt-32 pb-16 bg-[#09090b]">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm mb-6">
              <Lock className="w-4 h-4" />
              Legal
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
              Security & Infrastructure
            </h1>
            <p className="text-lg text-zinc-400">
              Last Updated: March 18, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Intro callout */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mb-8">
            <div className="border-l-4 border-emerald-500 pl-6">
              <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
                Security is not an afterthought at Furistic — it is embedded in
                our architecture, our development practices, and our culture.
                This page describes how we protect your data, our
                infrastructure, and our commitment to transparency in security
                matters.
              </p>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.num}
                  className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-emerald-400">
                        {section.num}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-emerald-400" />
                      <h2 className="text-xl md:text-2xl font-bold text-zinc-900 dark:text-white">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  <div className="text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-line pl-14">
                    {section.content}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact */}
          <div className="bg-white dark:bg-zinc-900/50 rounded-2xl p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 mt-8">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-4">
              Security Contact
            </h2>
            <p className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
              For security concerns, vulnerability reports, or to request our
              security documentation, contact us at{" "}
              <a
                href="mailto:security@furistic.co"
                className="text-violet-400 hover:text-violet-300 underline"
              >
                security@furistic.co
              </a>
              .
            </p>
          </div>

          {/* Other Legal Pages */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider mb-4">
              Other Legal Pages
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  href: "/legal/terms",
                  label: "Terms of Service",
                  icon: FileText,
                },
                {
                  href: "/legal/privacy",
                  label: "Privacy Policy",
                  icon: Shield,
                },
                {
                  href: "/legal/disclaimer",
                  label: "AI Disclaimer",
                  icon: AlertTriangle,
                },
              ].map((link) => {
                const LIcon = link.icon;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-violet-500/30 transition group"
                  >
                    <LIcon className="w-4 h-4 text-violet-400" />
                    <span className="text-sm text-zinc-300 group-hover:text-white transition">
                      {link.label}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-400 ml-auto transition" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
